import { useEffect, useRef, useState, type ReactElement } from 'react'
import { AppShell } from '../components/AppShell.js'
import { DetectionBox } from '../components/installer/DetectionBox.js'
import { Icon } from '../components/installer/icons.js'
import { HIGHLOOP_FAIL, PROOF_STEPS } from '../lib/proofSteps.js'
import { useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

/**
 * Scripted camera viewfinder for a single proof step. There is no real camera
 * and no AI: a primer overlay, an AR-lock simulation, an analyzing overlay, and
 * a flash are all driven by `setTimeout`s over a pre-baked photo with an
 * app-rendered bounding box. The signature beat is the high-loop fail path:
 * the first `highloop` attempt routes to `verify-failed`; the retake passes.
 *
 * Ported from the Lovable prototype `Index.tsx` `screen-camera` (lines 490-595)
 * and its timer effects (lines 156-205).
 *
 * Timer cleanup is load-bearing — see the unmount effect below.
 */
export const CaptureScreen = (): ReactElement | null => {
  const { state, dispatch } = useDemoStore()
  const { goto } = useStep()

  // Local UI state — deliberately NOT in the store. A fresh mount (the router
  // unmounts CaptureScreen between photos) resets these via the initializers,
  // so there is no cross-photo reset effect.
  const [primerVisible, setPrimerVisible] = useState(true)
  const [arLocked, setArLocked] = useState(false)
  const [framingPromptText, setFramingPromptText] = useState('Hold steady · aligning')
  const [flashing, setFlashing] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)

  // All pending timers live in refs so the unmount cleanup can clear every one.
  // If any timer is still pending when the component unmounts (user taps Back
  // mid-sequence), clearing it here guarantees its callback never runs — so no
  // local `setState` ever fires after unmount.
  const lockTimerRef = useRef<number | null>(null)
  const flashOffTimerRef = useRef<number | null>(null)
  const analyzeOnTimerRef = useRef<number | null>(null)
  const resolveTimerRef = useRef<number | null>(null)

  // Guard against double-tap: set to true synchronously when capturePhoto
  // begins, so a second tap within the 200ms pre-analyzing window is a no-op.
  // No reset needed — a successful capture navigates away and unmounts; a fresh
  // mount (or a clean activeIdx change) gets a fresh ref value of false.
  const capturingRef = useRef<boolean>(false)

  useEffect(() => {
    return () => {
      for (const ref of [
        lockTimerRef,
        flashOffTimerRef,
        analyzeOnTimerRef,
        resolveTimerRef,
      ]) {
        if (ref.current !== null) {
          clearTimeout(ref.current)
          ref.current = null
        }
      }
    }
  }, [])

  // Reset local camera state whenever the active step index changes. This makes
  // the screen correct whether or not the router remounts it between photos.
  // Idempotent on a fresh mount (state already matches the initializers).
  useEffect(() => {
    setPrimerVisible(true)
    setArLocked(false)
    setFramingPromptText('Hold steady · aligning')
    setFlashing(false)
    setAnalyzing(false)
    capturingRef.current = false
  }, [state.activeIdx])

  // ── All hooks are above this guard. ──────────────────────────────────────
  const activeStep = PROOF_STEPS[state.activeIdx]
  if (activeStep === undefined) return null

  const showHighloopFail = activeStep.id === 'highloop' && !state.highloopAttempted
  const cameraImage = showHighloopFail ? HIGHLOOP_FAIL.photo : activeStep.basePhoto
  const cameraBoxSpec = showHighloopFail ? HIGHLOOP_FAIL.cameraBox : activeStep.cameraBox[0]
  const arVariant = arLocked ? (showHighloopFail ? 'fail' : 'locked') : 'scanning'

  const dismissPrimer = (): void => {
    setPrimerVisible(false)
    if (lockTimerRef.current !== null) clearTimeout(lockTimerRef.current)
    lockTimerRef.current = window.setTimeout(() => {
      setArLocked(true)
      setFramingPromptText('Framing locked · tap to capture')
    }, 1200)
  }

  const capturePhoto = (): void => {
    if (!arLocked || analyzing || capturingRef.current) return
    capturingRef.current = true
    setFlashing(true)
    flashOffTimerRef.current = window.setTimeout(() => setFlashing(false), 120)
    analyzeOnTimerRef.current = window.setTimeout(() => setAnalyzing(true), 200)
    resolveTimerRef.current = window.setTimeout(() => {
      setAnalyzing(false)
      // dispatch + goto fire unconditionally: the store and the URL survive
      // unmount, so there is no React state to corrupt. (And in practice this
      // callback cannot run post-unmount anyway — the unmount cleanup clears
      // this timer first.)
      if (activeStep.id === 'highloop' && !state.highloopAttempted) {
        dispatch({ type: 'HIGHLOOP_ATTEMPTED' })
        goto('verify-failed')
      } else {
        dispatch({ type: 'MARK_CAPTURED', id: activeStep.id })
        goto('verify-passed')
      }
    }, 1800)
  }

  return (
    <AppShell fullbleed>
      {/* installer-surface must be an ANCESTOR of .camera-screen — every installer.css
          rule is descendant-scoped (`.installer-surface .camera-screen`), so putting both
          classes on one element silently drops the camera's entire flex layout (body
          collapses to height 0). The wrapper fills the fullbleed (relative) main. */}
      <div className="installer-surface" style={{ position: 'absolute', inset: 0 }}>
        <div className={`camera-screen ${primerVisible ? 'primer-active' : ''}`}>
        <div className="camera-header">
          <button className="back" onClick={() => goto('proof-bundle')} aria-label="Back">
            <Icon.Back />
          </button>
          <div className="title">{activeStep.label}</div>
          <div className="privacy-badge">
            <Icon.ShieldPlain size={12} />
            Privacy mask on
          </div>
        </div>

        <div className="camera-body">
          <div className="camera-bg" style={{ backgroundImage: `url(${cameraImage})` }} />

          {/* Primer overlay */}
          <div className={`primer-overlay ${primerVisible ? 'visible' : ''}`}>
            <div className="primer-card">
              <div className="primer-preview">
                <div
                  className="primer-preview-scene"
                  style={{
                    backgroundImage: `url(${activeStep.basePhoto})`,
                    backgroundPosition:
                      activeStep.id === 'highloop'
                        ? 'center -40px'
                        : activeStep.id === 'drain'
                          ? 'center 20%'
                          : 'center',
                  }}
                >
                  {activeStep.primerBox.map((box) => (
                    <div
                      key={`${box.text}-${box.top}-${box.left}`}
                      className="primer-sample-box"
                      style={{
                        top: box.top,
                        left: box.left,
                        width: box.width,
                        height: box.height,
                      }}
                    >
                      <div className="primer-sample-label">{box.text}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="primer-title">{activeStep.label}</div>
              <div className="primer-section-label">WHAT TO CAPTURE</div>
              <ul className="primer-list">
                {activeStep.capture.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <div className="primer-why">
                <div className="primer-why-label">WHY THIS MATTERS</div>
                <div className="primer-why-body">{activeStep.why}</div>
              </div>
              <button className="primer-btn" onClick={dismissPrimer}>
                <Icon.Camera /> Ready to capture
              </button>
            </div>
          </div>

          {/* AR box */}
          {cameraBoxSpec !== undefined && (
            <DetectionBox box={cameraBoxSpec} variant={arVariant} />
          )}

          {/* Framing prompt */}
          <div className={`framing-prompt ${arLocked ? 'locked' : ''}`}>
            <Icon.Clock size={14} />
            <span>{framingPromptText}</span>
          </div>

          {/* Analyzing overlay */}
          <div className={`analyzing ${analyzing ? 'visible' : ''}`}>
            <div className="analyzing-spinner" />
            <div className="analyzing-text">Analyzing…</div>
            <div className="analyzing-sub">Running {activeStep.label.toLowerCase()} check</div>
          </div>

          {/* Flash */}
          <div className={`camera-capturing ${flashing ? 'flash' : ''}`} />
        </div>

        <div className="camera-controls">
          <div className="flashlight">
            <Icon.Flash />
          </div>
          <div className="camera-shutter" onClick={capturePhoto}>
            <div className="camera-shutter-inner">
              <Icon.Camera size={22} />
            </div>
          </div>
          <div style={{ width: 36, height: 36 }} />
        </div>
        </div>
      </div>
    </AppShell>
  )
}
